import { effect, Injectable } from '@angular/core';
import { Ballot } from '../ballot/backend-types';
import {
  MteStatus,
  MteMkeEnc,
  MteMkeDec,
  MteStrStatus,
} from "mte";
import { MteService, mteBase, mteWasm, mteStates, randomUUID } from './mte.service';
import { DeviceProfile } from '../backend-types/misc-types';

/**
 * Right now this is a thin wrapper for the SessionStorage object.
 * 
 * We can more easily add encrpytion to sensitive data by having 
 * all the stored objects in the same class that go through the same
 * `getItem` and `setItem` wrapper functions.
 *
 * @export
 * @class SessionStorageService
 */
@Injectable({
  providedIn: 'root'
})
export class SessionStorageService {

  ballotKey = "ballot";
  ballotListKey = "ballotList";
  currentContestKey = "currentContest";
  sessionSymmetricStateKey = "sessionSymmetricState";
  deviceProfileKey = "deviceProfile";

  mteService: MteService;

  sessionMteStates: mteStates = {encoderState: "", decoderState: ""};
  sessionDecoder: MteMkeDec | undefined;
  sessionEncoder: MteMkeEnc | undefined;

  sessionSymmetricState: string | null = ""; 

  constructor(mteService: MteService) {
    this.mteService = mteService;
    this.removeEncryptedItems();

    effect(() => {
      if(mteService.mteInitialized()) {
        this.initializeMasterState();
        this.setSessionSymmetricState(this.sessionMteStates.decoderState);
      }
    })  
  }

  initializeMasterState() {
    let encoderEntropy: Uint8Array = window.crypto.getRandomValues(new Uint8Array(32));
    let encoderNonce: string = randomUUID();
    let encoderPersonalization: string = Date.now().toString();

    this.sessionMteStates = this.mteService.createInitialMteState(
      encoderEntropy,
      encoderNonce,
      encoderPersonalization,
      new Uint8Array(encoderEntropy),
      encoderNonce,
      encoderPersonalization
    )
    this.initializeCoders();
  }

  initializeCoders() {
    this.sessionDecoder = MteMkeDec.fromdefault(mteWasm);
    this.sessionEncoder = MteMkeEnc.fromdefault(mteWasm);
  }

  private getItem(key: string): string | null {
    const encodedValue: string | null = sessionStorage.getItem(key);
    if(encodedValue) {
      if(!this.sessionDecoder){
        this.sessionDecoder = MteMkeDec.fromdefault(mteWasm);
      }
      if(!this.sessionSymmetricState){
        this.sessionSymmetricState = this.getSessionSymmetricState();
      }
      if(!this.sessionSymmetricState){
        console.error("Could not retrieve session symmetric state.");
        throw new Error("Could not retrieve session symmetric state.");
      }
      this.sessionDecoder.restoreStateB64(this.sessionSymmetricState);
      let decodedValue: MteStrStatus = this.sessionDecoder.decodeStrB64(encodedValue);
      if (decodedValue.status !== MteStatus.mte_status_success && mteBase) {
        const status = mteBase.getStatusName(decodedValue.status);
        const message = mteBase.getStatusDescription(decodedValue.status);
        console.error(`Error when MKE decoding string.\n${status}: ${message}`);
        return null;
      }
      return decodedValue.str;
    }
    else {
      return null;
    }
  }

  // Get an (unencrypted) item from session storage.
  // This is not for sensitive data.
  private getItemPlain(key: string): string | null {
    return sessionStorage.getItem(key)
  }

  getBallot(): Ballot | undefined {
    const ballotItem = this.getItem(this.ballotKey)
    return ballotItem ? JSON.parse(ballotItem) as Ballot : undefined
  }

  getBallotList(): Ballot[] | undefined {
    const listItem = this.getItem(this.ballotListKey)
    return listItem ? JSON.parse(listItem) as Ballot[] : undefined
  }

  getCurrentContest(): number | undefined {
    const contestItem = this.getItemPlain(this.currentContestKey)
    return contestItem ? +contestItem : undefined
  }

  getSessionSymmetricState(): string | null {
    if(this.sessionSymmetricState){
      return this.sessionSymmetricState;
    }
    else {
      this.sessionSymmetricState = this.getItemPlain(this.sessionSymmetricStateKey);
      return this.sessionSymmetricState;
    }
  }

  getDeviceProfile(): DeviceProfile {
    return JSON.parse(this.getItemPlain(this.deviceProfileKey) ?? '{"initialNextKey":0}');
  }

  removeAll(): void {
    sessionStorage.clear()
  }

  removeEncryptedItems(): void {
    sessionStorage.removeItem(this.ballotKey);
    sessionStorage.removeItem(this.ballotListKey);
  }

  private async setItem(key: string, item: string): Promise<void> {
    if(!this.sessionEncoder){
      this.sessionEncoder = MteMkeEnc.fromdefault(mteWasm);
    }
    if(!this.sessionSymmetricState){
      this.sessionSymmetricState = this.getSessionSymmetricState();
    }
    if(!this.sessionSymmetricState){
      console.error("Could not retrieve session symmetric state.");
      return Promise.reject("Could not retrieve session symmetric state.");
    }
    this.sessionEncoder.restoreStateB64(this.sessionSymmetricState);
    const encodedItem = this.sessionEncoder.encodeStrB64(item);
    if (encodedItem.status !== MteStatus.mte_status_success && mteBase) {
      const status = mteBase.getStatusName(encodedItem.status);
      const message = mteBase.getStatusDescription(encodedItem.status);
      console.error(`Error when MKE encoding string.\n${status}: ${message}`);
    }
    if(encodedItem.str) {
      sessionStorage.setItem(key, encodedItem.str)
    } else {
      console.error("Attempted to store null value.");
    }
  }

  private async setItemPlain(key: string, item: string): Promise<void> {
    sessionStorage.setItem(key, item)
  }

  async setBallot(ballot: Ballot): Promise<void> {
    const ballotItem = JSON.stringify(ballot)
    this.setItem(this.ballotKey, ballotItem)
  }

  async setBallotList(ballots: Ballot[]): Promise<void> {
    const ballotsItem = JSON.stringify(ballots)
    this.setItem(this.ballotListKey, ballotsItem)
  }

  async setCurrentContest(currentContest: number): Promise<void> {
    const currentContestItem = currentContest.toString()
    this.setItemPlain(this.currentContestKey, currentContestItem)
  }

  async setSessionSymmetricState(symmetricState: string | null): Promise<void> {
    this.sessionSymmetricState = symmetricState;
    this.setItemPlain(this.sessionSymmetricStateKey, symmetricState ? symmetricState : "");
  }

  async setDeviceProfile(profile: DeviceProfile) {
    await this.setItemPlain(this.deviceProfileKey, JSON.stringify(profile))
  }
}
